import { useForm, Controller } from 'react-hook-form';
import { object, string } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';
import { useHistory, useLocation } from 'react-router-dom';
import { Form, ValidateField, VerificationInputField, ErrorSpace, ErrorText, VerifyButton, BackToLoginButton, ResendCodeLink } from './VerificationForm.style';
import codeIcon from '../../../assets/images/icons/password-key.svg';
import { verifyRegistrationCode, resendVerificationCode } from '../../../services/userRegistration.service';

const VerificationForm = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  // Get the identificator from the location state (passed from registration)
  const identificator = location.state?.identificator;

  const validationSchema = object().shape({
    code: string()
      .required(t('verification.codeIsRequired'))
      .matches(/^\d{6}$/, t('verification.codeMustBe6Digits'))
  });

  const {
    formState: { errors },
    control,
    handleSubmit
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onBlur'
  });

  const onSubmit = async ({ code }) => {
    try {
      if (!identificator) {
        message.error(t('verification.noIdentificator'));
        return;
      }
      
      const response = await verifyRegistrationCode({
        identificator,
        code
      });
      
      
      message.success(t('verification.successMessage'));
      history.push('/');
    } catch (error) {
      console.error('Verification error:', error);
      
      // Handle different types of errors
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.response?.status === 400) {
        message.error(t('verification.invalidCode'));
      } else if (error.response?.status === 404) {
        message.error(t('verification.identificatorNotFound'));
      } else {
        message.error(t('verification.failedVerification'));
      }
    }
  };

  const handleResendCode = async () => {
    try {
      if (!identificator) {
        message.error(t('verification.noIdentificator'));
        return;
      }
      
      const response = await resendVerificationCode({
        identificator
      });
      
      
      message.success(t('verification.codeResentMessage'));
    } catch (error) {
      console.error('Resend code error:', error);
      
      // Handle different types of errors
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.response?.status === 404) {
        message.error(t('verification.identificatorNotFound'));
      } else {
        message.error(t('verification.failedResendCode'));
      }
    }
  };

  const handleBackToLogin = () => {
    history.push('/');
  };

  // If no identificator is available, show error
  if (!identificator) {
    return (
      <Form>
        <div style={{ color: 'white', textAlign: 'center', marginBottom: '20px' }}>
          {t('verification.noIdentificator')}
        </div>
        <BackToLoginButton type="button" onClick={handleBackToLogin}>
          {t('verification.backToLogin')}
        </BackToLoginButton>
      </Form>
    );
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div style={{ color: 'white', textAlign: 'center', marginBottom: '20px' }}>
        {t('verification.enterCodeMessage')}
      </div>
      <Controller
        key="code"
        name="code"
        defaultValue=""
        control={control}
        render={({ field }) => (
          <ValidateField>
            <VerificationInputField
              prefix={<ReactSVG src={codeIcon} />}
              onChange={field.onChange}
              value={field.value}
              placeholder={t('verification.code').toUpperCase()}
              maxLength={10}
            />
            <ErrorSpace>{errors.code && <ErrorText>{errors.code.message}</ErrorText>}</ErrorSpace>
            <ResendCodeLink onClick={handleResendCode} tabIndex={0} role="button">
              {t('verification.resendCode')}
            </ResendCodeLink>
          </ValidateField>
        )}
      />
      <VerifyButton htmltype="submit">{t('verification.verify')}</VerifyButton>
      <BackToLoginButton type="button" onClick={handleBackToLogin}>
        {t('verification.backToLogin')}
      </BackToLoginButton>
    </Form>
  );
};

export default VerificationForm; 